// Core packages
import { Component, OnDestroy, AfterViewInit, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

// Third party packaged
import { MatDialog } from '@angular/material';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';

// Custom packages
import { DisclaimerDialogComponent } from 'src/app/components/disclaimer-dialog/disclaimer-dialog.component';
import { PrivacyDialogComponent } from "src/app/components/privacy-dialog/privacy-dialog.component";
import { AccessLogsService } from 'src/app/modules/access-logs/access-logs.service';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { ChooseRoleDialogComponent } from 'src/app/modules/auth/components/choose-role-dialog/choose-role-dialog.component';
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { sharedUserLanding } from 'src/app/modules/properties/document-archive-entries.utils';
import { RolesService } from 'src/app/modules/roles/roles.service';
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from 'src/app/shared/services/helper.service';
import { ThemeService } from 'src/app/shared/services/theme.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss']
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add property form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('auth.login.form.submitButtonText'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-sign-in',
      inline: true,
    },
  };
  // END - Add property form

  returnUrl: string;

  disclaimer: any = {};
  disclaimerWaiting = false;

  /**
   * Class constructor
   */
  constructor(
    private themeService: ThemeService,
    private authService: AuthService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public helperService: HelperService,
    public configService: ConfigService,
    public rolesService: RolesService,
    public companiesService: CompaniesService,
    private dialog: MatDialog,
    private accessLogsService: AccessLogsService,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Subscribe to current lang and init component
    this.lang = this.translateService.currentLang;

    // Save (if present) the previous requested url or the homepage url,
    // so we can redirect there after successful login
    this.returnUrl = this.activatedRoute.snapshot.queryParams.returnUrl || `/${this.lang}/app`;

    if ((this.activatedRoute.snapshot.queryParams.ARsignage
      && this.activatedRoute.snapshot.queryParams.ARsignage === "from_digital_signage")
      || (this.activatedRoute.snapshot.queryParams.ARmanagement
        && this.activatedRoute.snapshot.queryParams.ARmanagement === "from_management")) {
      if (this.activatedRoute.snapshot.queryParams.email
        && this.activatedRoute.snapshot.queryParams.password) {
        this.onFormSubmit({
          email: this.activatedRoute.snapshot.queryParams.email,
          password: this.activatedRoute.snapshot.queryParams.password
        });
      }
    }
    if (this.activatedRoute.params) {
      this.subscriptions.push(
        this.activatedRoute.params.subscribe(
          res => {
            if (res) {
              if (res.lang) {
                this.lang = res.lang;
              }
              this.handleRouteParamsChange();
            }
          },
          err => {
            // if we came here users with given userId do not exist
            this.router.navigate([this.lang, 'errors', '404']);
          }
        )
      );
    }
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // this.dialog.open(ConfirmDialogComponent, {
    //   data: {
    //     title: this.translateService.instant("Manutenzione programmata"),
    //     message: "Gentile Utente<br>Ti informiamo che a seguito di alcune migliorie tecniche che stiamo apportando alla piattaforma Archedi, il servizio potrebbe risultare rallentato e alcune funzionalità potrebbero essere inibite.<br><br>Ci scusiamo per il disagio e rimaniamo a disposizione per eventuali chiarimenti.<br><br>Cordiali saluti<br>Team Archedi<br>",
    //     btnOkText: this.translateService.instant("common.cta.close"),
    //     btnCancelText: this.translateService.instant("common.cta.close"),
    //     messageOnly: true,
    //   },
    //   width: "500px",
    //   disableClose: true,
    // });
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init component
   */
  handleRouteParamsChange(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('auth.login.pageTitle'));

    // Prevent form fields to be rendered twice on language change
    this.fields = [];

    // Initialize form
    this.initForm();

    // Set correct button text based on current language
    this.formBtnOptions.text = this.translateService.instant('auth.login.form.submitButtonText');

    // Check if user is already logged in
    // with authservice
    // If so, redirect him to to previously requested url
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((user) => {
        if (user) {
          this.loggedUser = user;
          if (!this.disclaimerWaiting){
            // this.router.navigate([this.lang, 'app']);
            this.router.navigateByUrl(this.returnUrl);
          }
        }
      })
    );

    // Check if user is logged in with cookies
    // data. If so, redirect him
    this.subscriptions.push(
      this.authService.checkLogin()
        .subscribe(
          res => {
            if (res.status) {
              // User already logged, redirect him to home
              const lang = this.translateService.currentLang;
              // this.router.navigate([lang, 'app']);
              this.router.navigateByUrl(this.returnUrl);
            }
          }
        )
    );
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = {
      email: '',
      password: '',
    };

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12',
            type: 'input',
            key: 'email',
            templateOptions: {
              label: this.translateService.instant('auth.login.form.email.label'),
              placeholder: this.translateService.instant('auth.login.form.email.placeholder'),
              required: true,
              maxLength: 320,
              type: 'email',
              pattern: this.configService.emailPattern,
            },
          },
          {
            className: 'col-10',
            type: 'input',
            key: 'password',
            templateOptions: {
              label: this.translateService.instant('auth.login.form.password.label'),
              placeholder: this.translateService.instant('auth.login.form.password.placeholder'),
              required: true,
              type: 'password',
            },
          },
          {
            className: 'col-2',
            type: 'button',
            key: 'showHidePassword',
            templateOptions: {
              color: "accent",
              tooltip: "tooltip",
              icon: {
                fontSet: "fas",
                fontIcon: "fa-eye",
              },
              onClick: ($event) =>
                this.showHidePassword(),
            },
          }
        ],
      },
    ];
  }

  /**
   * Handle show and hide passwword field
   *
   * @since 1.0.0
   */
  showHidePassword(): void {
    if (this.fields && Array.isArray(this.fields)) {
      this.fields.forEach((el: any) => {
        if (el.fieldGroup && Array.isArray(el.fieldGroup)) {
          let index = el.fieldGroup.findIndex((subEl: any) => {
            return subEl.key === 'password';
          });
          if (
            index > -1 && 
            el.fieldGroup[index].templateOptions &&
            el.fieldGroup[index].templateOptions.type
          ) {
            if (el.fieldGroup[index].templateOptions.type === 'text') {
              el.fieldGroup[index].templateOptions.type = 'password';
            } else {
              el.fieldGroup[index].templateOptions.type = 'text';
            }
          };
          index = el.fieldGroup.findIndex((subEl: any) => {
            return subEl.key === 'showHidePassword';
          });
          if (
            index > -1 &&
            el.fieldGroup[index].templateOptions &&
            el.fieldGroup[index].templateOptions.icon &&
            el.fieldGroup[index].templateOptions.icon.fontIcon
          ) {
            if (el.fieldGroup[index].templateOptions.icon.fontIcon === 'fa-eye') {
              el.fieldGroup[index].templateOptions.icon.fontIcon = 'fa-eye-slash';
            } else {
              el.fieldGroup[index].templateOptions.icon.fontIcon = 'fa-eye';
            }
          };
        }
      });
    }
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      return;
    }

    this.disclaimerWaiting = true;

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    this.subscriptions.push(
      this.authService.login(model.email, model.password)
        .pipe(
          finalize(() => this.formBtnOptions.active = false)
        )
        .subscribe(
          res => {
            if (res.status) {
              const user = res.user;
              const accessId = res.accessId;

              // If user has a default password (first access), prevent role choose
              if (user && user.originalPassword) {
                this.router.navigate([this.lang, 'app'])
                return;
              }
              // If user has more than 1 role, ask for which one he wants to login
              if (!(user && user.userRoles) || user.userRoles.length > 1) {
                const dialogRef = this.dialog.open(ChooseRoleDialogComponent, {
                  data: {
                    user: this.loggedUser,
                  }
                });
                // Subscribe to dialog result (only for 1 emit thanks to "take()")
                dialogRef.beforeClosed()
                  .pipe(
                    take(1)
                  )
                  .subscribe(
                    res2 => {
                      const id = res2._id;
                      const roleId = res2.role._id;
                      const roleType = res2.role.type;
                      const companyId = res2.company ? res2.company._id : undefined;
                      const privacy = res2.privacy;

                      if (roleType === "temporary" && (typeof privacy === 'undefined' || !privacy)) {
                        const dialogRef = this.dialog.open(
                          PrivacyDialogComponent,
                          {
                            data: {
                              userId: this.loggedUser._id,
                              roleId: id,
                            },
                            width: "800px",
                            disableClose: true,
                          }
                        );
                        dialogRef
                          .beforeClosed()
                          .pipe(take(1))
                          .subscribe((res) => {
                            if (!res) {
                              this.authService.logout(true);
                            }
                          });
                        
                        this.disclaimerWaiting = false;
                        return;
                      }
                      if (companyId) {
                        this.subscriptions.push(
                          this.companiesService
                            .get(companyId)
                            .subscribe(
                              (companyRes) => {
                                this.disclaimerWaiting = false;
                                if (companyRes.data && companyRes.data.disclaimer) {
                                  this.disclaimer = companyRes.data.disclaimer;
                                }
                                if (
                                  companyRes.status &&
                                  companyRes.data &&
                                  (
                                    !companyRes.data.disclaimer ||
                                    !companyRes.data.disclaimer.check1 ||
                                    !companyRes.data.disclaimer.check2 ||
                                    !companyRes.data.disclaimer.check3 ||
                                    !companyRes.data.disclaimer.check4
                                  )
                                ) {
                                  const loggedUserId = this.loggedUser._id.toString();
                                  if (
                                    // this.loggedUser._id === '5d974025f0cd540fa4bd2d48' || // SITI
                                    // this.loggedUser._id === '5f85adf518e3cf63c87933d9' || // SITI fabio
                                    // this.loggedUser._id === '60c8586211ab7c61927de461' || // cinzia.archedi@gmail.com
                                    // // this.loggedUser._id === '5f7dd2d8172e822bd0477be6' || // daniele.spasaro@archedi.it
                                    // // this.loggedUser._id === '5f7dd2d8172e822bd0477ba0' || // daniele.spasaro@gmail.com
                                    // // this.loggedUser._id === '5f7dd2d8172e822bd0477c99' || // daniele.spasaro@studioassociatogamma.com
                                    // this.loggedUser._id === '5ff8154d6e0b8d388c7d8fc2' || // moseva2013@gmail.com
                                    // this.loggedUser._id === '5ef210e3e5da4742563f4c75' || // info@archedi.it
                                    // this.loggedUser._id === '60631f3166a6a849c4462d20' || // segreteria@archedi.it
                                    // this.loggedUser._id === '5f7dd2d8172e822bd0477bd3' || // vanessa.c.archedi@gmail.com
                                    // this.loggedUser._id === '6176a4df0ab08f064e35244f' || // vanessa.cicognani@studioassociatogamma.com
                                    // this.loggedUser._id === '5f7dd2d8172e822bd0477e44' || // cinzia.ragusag@gmail.com
                                    // this.loggedUser._id === '6176a5190ab08f064e352452' || //cinzia.ragusa@studioassociatogamma.com
                                    // this.loggedUser._id === '5f7dd2d8172e822bd0477e44'    // cinzia.ragusag@gmail.com

                                    loggedUserId !== '5d974025f0cd540fa4bd2d48' && // info@sitisrl.it
                                    loggedUserId !== '5f7dd2d8172e822bd0477b92' && // archedi@archedi.it
                                    loggedUserId !== '5f7dd2d8172e822bd0477be6' && // daniele.spasaro@archedi.it
                                    loggedUserId !== '5f86fb7bf06c117f1a0d5875' && // francescobancalari@gmail.com
                                    loggedUserId !== '5ff8154d6e0b8d388c7d8fc2' && // moseva2013@gmail.com
                                    loggedUserId !== '60acf503c3fac95703a36d8e' && // luca.sarti@oneam.it
                                    loggedUserId !== '60c8586211ab7c61927de461' && // cinzia.archedi@gmail.com
                                    loggedUserId !== '613a1237db63672bc5444677' && // rboccasso@gmail.com
                                    loggedUserId !== '5ef210e3e5da4742563f4c75' && // info@archedi.it
                                    loggedUserId !== '5f7dd2d8172e822bd0477bd7' && // laura.s.archedi@gmail.com
                                    loggedUserId !== '5f7dd2d8172e822bd0477c6c' && // info@asgsrl.com
                                    loggedUserId !== '5f7dd2d8172e822bd0477d2f' && // paolofrigieri@icloud.com
                                    loggedUserId !== '5f7dd2d8172e822bd0477d37' && // paolasantambrogio@tin.it
                                    loggedUserId !== '5f7dd2d8172e822bd0477e48' && // info@studioassociatogamma.com
                                    loggedUserId !== '5f7dd2d9172e822bd0478018' && // anna.garbelli@gmail.com
                                    loggedUserId !== '5f85adf518e3cf63c87933d9' && // fabio.stangalini@sitisrl.it
                                    loggedUserId !== '60f0467bda984c5931b22d09' && // m.coti@studioassociatogamma.com
                                    loggedUserId !== '6139cd12db63672bc54411f8' && // gianlorenzo.zardini@gmail.com
                                    loggedUserId !== '61404eb4db63672bc54e027c' && // scilla.ioppolo@tiscali.it
                                    loggedUserId !== '6143472f6dc6100ff790dc31' && // marco.deangeli@studioassociatogamma.com
                                    loggedUserId !== '61f7df04188a702dec5610bd' && // nicolo.felice@studioassociatogamma.com
                                    loggedUserId !== '6267c3363d13ce3a6a78918f' && // annalisa.mannucci@studioassociatogamma.com
                                    loggedUserId !== '629da9fd7003cc64a43fd80f' && // masamatteo45@gmail.com
                                    loggedUserId !== '63fc7cc75ef4cf243c55be32' && // irenemanzoni@gmail.com
                                    loggedUserId !== '641071673ec7974b112d4a77' && // silviadonato10@gmail.com
                                    loggedUserId !== '648704bbad4b1e55999d09eb' && // sil.lai.lucia@gmail.com
                                    loggedUserId !== '649aa862d981314352bd0f24' && // marcomari@pegasopc.it
                                    loggedUserId !== '649aa8b4d981314352bd0f26' && // mattiabarbetta.mb@gmail.com
                                    loggedUserId !== '64a7ecc468d7703d3b7e62e4' && // c.cacopardi@gmail.com
                                    loggedUserId !== '6501d1ed57eaad6f3cdf079c' && // arch.margherita.corsi@gmail.com
                                    loggedUserId !== '653bcab99861152e10496db7' // margherita.corsi@archedi.it
                                  ) {
                                    if (roleType === "master") {
                                      const dialogRef = this.dialog.open(
                                        DisclaimerDialogComponent,
                                        {
                                          data: {
                                            companyId,
                                          },
                                          width: "800px",
                                          disableClose: true,
                                        }
                                      );
                                      dialogRef
                                        .beforeClosed()
                                        .pipe(take(1))
                                        .subscribe((res) => {
                                          if (!res) {
                                            this.authService.logout(true);
                                          }
                                        });
                                      return;
                                    } else if (roleType === "collaborator") {
                                      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                                        data: {
                                          title: this.translateService.instant("dialogs.disclaimer.title"),
                                          message: this.translateService.instant(
                                            "dialogs.disclaimer.messageToCollaborator"
                                          ),
                                          btnOkText: this.translateService.instant("common.cta.close"),
                                          btnCancelText: this.translateService.instant("common.cta.close"),
                                          messageOnly: true,
                                        },
                                        width: "500px",
                                        disableClose: true,
                                      });
                                      dialogRef
                                        .beforeClosed()
                                        .pipe(take(1))
                                        .subscribe(() => {
                                          this.authService.logout(true);
                                        });
                                      return;
                                    }
                                  }
                                }
                                return;
            
                              },
                              (err) => {
                                this.disclaimerWaiting = false;
                                this.helperService.handleError(err);
                              }
                            )
                        );
                      }

                      this.authService.setRequestRoleId(id, roleId);
                      this.accessLogsService.update(accessId, roleType, companyId).subscribe(
                        res3 => {
                          if (roleType === "temporary") {
                            this.disclaimerWaiting = false;
                            setTimeout(() => {
                              this.router.navigate([this.lang, 'app', 'properties', 'list'])
                            }, 600);
                          }
                          if (roleType === "shared") {
                            this.disclaimerWaiting = false;
                            setTimeout(() => {
                              // my-property until the switch, the new archive of the role's property after it
                              this.router.navigate(sharedUserLanding(this.lang, res2));
                            }, 600);
                          }
                        },
                        err3 => {
                          this.disclaimerWaiting = false;
                          this.helperService.handleError(err3);
                        }
                      );
                    }
                  );
                // Ok, now we know user has ben logged and data has
                // been saved in cookies. We can redirect him!
                const lang = this.translateService.currentLang;
                this.router.navigate([lang, 'app']);
              } else {
                const uniqueRole = user.userRoles[0];
                const roleId = uniqueRole.role._id;
                const roleType = uniqueRole.role.type;
                const companyId = uniqueRole.company ? uniqueRole.company._id : undefined;
                const privacy = uniqueRole.privacy;

                if (roleType === "temporary" && (typeof privacy === 'undefined' || !privacy)) {
                  const dialogRef = this.dialog.open(
                    PrivacyDialogComponent,
                    {
                      data: {
                        userId: user._id,
                        roleId: uniqueRole._id,
                      },
                      width: "800px",
                      disableClose: true,
                    }
                  );
                  dialogRef
                    .beforeClosed()
                    .pipe(take(1))
                    .subscribe((res) => {
                      if (!res) {
                        this.authService.logout(true);
                      }
                    });

                  this.disclaimerWaiting = false;
                  // return;
                }

                this.authService.setRequestRoleId(uniqueRole._id, roleId);
                this.accessLogsService.update(accessId, roleType, companyId);
                if (roleType === "temporary") {
                  setTimeout(() => {
                    this.disclaimerWaiting = false;
                    this.router.navigate([this.lang, 'app', 'properties', 'list'])
                  }, 600);
                }
                if (roleType === "shared") {
                  const propertyId = uniqueRole.propertiesPermission.property[0];
                  setTimeout(() => {
                    this.disclaimerWaiting = false;
                    this.router.navigate([this.lang, 'app', 'properties', propertyId, 'home']);
                  }, 600);
                }

                if (companyId) {
                  this.subscriptions.push(
                    this.companiesService
                      .get(companyId)
                      .subscribe(
                        (companyRes) => {
                          this.disclaimerWaiting = false;
                          if (companyRes.data && companyRes.data.disclaimer) {
                            this.disclaimer = companyRes.data.disclaimer;
                          }
                          if (
                            companyRes.status &&
                            companyRes.data &&
                            (
                              !companyRes.data.disclaimer ||
                              !companyRes.data.disclaimer.check1 ||
                              !companyRes.data.disclaimer.check2 ||
                              !companyRes.data.disclaimer.check3 ||
                              !companyRes.data.disclaimer.check4
                            )
                          ) {
                            const loggedUserId = this.loggedUser._id.toString();
                            if (
                              // this.loggedUser._id === '5d974025f0cd540fa4bd2d48' || // SITI
                              // this.loggedUser._id === '5f85adf518e3cf63c87933d9' || // SITI fabio
                              // this.loggedUser._id === '60c8586211ab7c61927de461' || // cinzia.archedi@gmail.com
                              // // this.loggedUser._id === '5f7dd2d8172e822bd0477be6' || // daniele.spasaro@archedi.it
                              // // this.loggedUser._id === '5f7dd2d8172e822bd0477ba0' || // daniele.spasaro@gmail.com
                              // // this.loggedUser._id === '5f7dd2d8172e822bd0477c99' || // daniele.spasaro@studioassociatogamma.com
                              // this.loggedUser._id === '5ff8154d6e0b8d388c7d8fc2' || // moseva2013@gmail.com
                              // this.loggedUser._id === '5ef210e3e5da4742563f4c75' || // info@archedi.it
                              // this.loggedUser._id === '60631f3166a6a849c4462d20' || // segreteria@archedi.it
                              // this.loggedUser._id === '5f7dd2d8172e822bd0477bd3' || // vanessa.c.archedi@gmail.com
                              // this.loggedUser._id === '6176a4df0ab08f064e35244f' || // vanessa.cicognani@studioassociatogamma.com
                              // this.loggedUser._id === '5f7dd2d8172e822bd0477e44' || // cinzia.ragusag@gmail.com
                              // this.loggedUser._id === '6176a5190ab08f064e352452' || //cinzia.ragusa@studioassociatogamma.com
                              // this.loggedUser._id === '5f7dd2d8172e822bd0477e44'    // cinzia.ragusag@gmail.com

                              loggedUserId !== '5d974025f0cd540fa4bd2d48' && // info@sitisrl.it
                              loggedUserId !== '5f7dd2d8172e822bd0477b92' && // archedi@archedi.it
                              loggedUserId !== '5f7dd2d8172e822bd0477be6' && // daniele.spasaro@archedi.it
                              loggedUserId !== '5f86fb7bf06c117f1a0d5875' && // francescobancalari@gmail.com
                              loggedUserId !== '5ff8154d6e0b8d388c7d8fc2' && // moseva2013@gmail.com
                              loggedUserId !== '60acf503c3fac95703a36d8e' && // luca.sarti@oneam.it
                              loggedUserId !== '60c8586211ab7c61927de461' && // cinzia.archedi@gmail.com
                              loggedUserId !== '613a1237db63672bc5444677' && // rboccasso@gmail.com
                              loggedUserId !== '5ef210e3e5da4742563f4c75' && // info@archedi.it
                              loggedUserId !== '5f7dd2d8172e822bd0477bd7' && // laura.s.archedi@gmail.com
                              loggedUserId !== '5f7dd2d8172e822bd0477c6c' && // info@asgsrl.com
                              loggedUserId !== '5f7dd2d8172e822bd0477d2f' && // paolofrigieri@icloud.com
                              loggedUserId !== '5f7dd2d8172e822bd0477d37' && // paolasantambrogio@tin.it
                              loggedUserId !== '5f7dd2d8172e822bd0477e48' && // info@studioassociatogamma.com
                              loggedUserId !== '5f7dd2d9172e822bd0478018' && // anna.garbelli@gmail.com
                              loggedUserId !== '5f85adf518e3cf63c87933d9' && // fabio.stangalini@sitisrl.it
                              loggedUserId !== '60f0467bda984c5931b22d09' && // m.coti@studioassociatogamma.com
                              loggedUserId !== '6139cd12db63672bc54411f8' && // gianlorenzo.zardini@gmail.com
                              loggedUserId !== '61404eb4db63672bc54e027c' && // scilla.ioppolo@tiscali.it
                              loggedUserId !== '6143472f6dc6100ff790dc31' && // marco.deangeli@studioassociatogamma.com
                              loggedUserId !== '61f7df04188a702dec5610bd' && // nicolo.felice@studioassociatogamma.com
                              loggedUserId !== '6267c3363d13ce3a6a78918f' && // annalisa.mannucci@studioassociatogamma.com
                              loggedUserId !== '629da9fd7003cc64a43fd80f' && // masamatteo45@gmail.com
                              loggedUserId !== '63fc7cc75ef4cf243c55be32' && // irenemanzoni@gmail.com
                              loggedUserId !== '641071673ec7974b112d4a77' && // silviadonato10@gmail.com
                              loggedUserId !== '648704bbad4b1e55999d09eb' && // sil.lai.lucia@gmail.com
                              loggedUserId !== '649aa862d981314352bd0f24' && // marcomari@pegasopc.it
                              loggedUserId !== '649aa8b4d981314352bd0f26' && // mattiabarbetta.mb@gmail.com
                              loggedUserId !== '64a7ecc468d7703d3b7e62e4' && // c.cacopardi@gmail.com
                              loggedUserId !== '6501d1ed57eaad6f3cdf079c' && // arch.margherita.corsi@gmail.com
                              loggedUserId !== '653bcab99861152e10496db7' // margherita.corsi@archedi.it
                            ) {
                              if (roleType === "master") {
                                const dialogRef = this.dialog.open(
                                  DisclaimerDialogComponent,
                                  {
                                    data: {
                                      companyId,
                                    },
                                    width: "800px",
                                    disableClose: true,
                                  }
                                );
                                dialogRef
                                  .beforeClosed()
                                  .pipe(take(1))
                                  .subscribe((res) => {
                                    if (!res) {
                                      this.authService.logout(true);
                                    }
                                  });
                                return;
                              } else if (roleType === "collaborator") {
                                const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                                  data: {
                                    title: this.translateService.instant("dialogs.disclaimer.title"),
                                    message: this.translateService.instant(
                                      "dialogs.disclaimer.messageToCollaborator"
                                    ),
                                    btnOkText: this.translateService.instant("common.cta.close"),
                                    btnCancelText: this.translateService.instant("common.cta.close"),
                                    messageOnly: true,
                                  },
                                  width: "500px",
                                  disableClose: true,
                                });
                                dialogRef
                                  .beforeClosed()
                                  .pipe(take(1))
                                  .subscribe(() => {
                                    this.authService.logout(true);
                                  });
                                return;
                              }
                            }
                          }

                          // Ok, now we know user has ben logged and data has
                          // been saved in cookies. We can redirect him!
                          const lang = this.translateService.currentLang;
                          if (roleType === "temporary") {
                            // This response can arrive after the timed redirect above:
                            // don't send temporary users back to the home
                            this.router.navigate([lang, 'app', 'properties', 'list']);
                          } else {
                            this.router.navigate([lang, 'app']);
                          }
                          return;
      
                        },
                        (err) => {
                          this.disclaimerWaiting = false;
                          this.helperService.handleError(err);
                        }
                      )
                  );
                }
                if (roleType === "operator" || roleType === "admin") {
                  const lang = this.translateService.currentLang;
                  this.router.navigate([lang, 'app']);
                }
              }
              return;
            }

            this.disclaimerWaiting = false;
            // Check if back-end is up and is running
            this.toastrService.error(this.translateService.instant('common.errors.server_down'));
            return;
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.disclaimerWaiting = false;
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }
}
